import { useCallback, useRef, useState } from 'react'

import {
  ExistingFile,
  GoodieFile,
  isDirtyFile,
  isGoodieFile,
  isNewFile
} from '../types'

import { batchReplaceNestedFieldsWithIds } from '@/utils/batchReplaceNestedFields'
import useUploadNewFile from './useUploadFile'
import useUpdateDirtyFile from './useUpdateDirtyFile'

export function useUpdateFiles(): [
  (files: GoodieFile[]) => Promise<ExistingFile[]>,
  {
    loading: boolean
    progress: number | null
  }
] {
  const [loading, setLoading] = useState(false)
  const [upload] = useUploadNewFile()
  const [updateDirtyFile] = useUpdateDirtyFile()
  const [progress, setProgress] = useState<number | null>(null)
  const progressMap = useRef(new Map<number, number>())

  const onUpdateProgress = useCallback(
    (index: number, progress: number) => {
      progressMap.current.set(index, progress)

      const size = progressMap.current.size

      const sum = progressMap.current
        .values()
        .reduce((sum, curr) => sum + curr, 0)

      setProgress(Math.round(sum / size))
    },
    [setProgress]
  )

  const update = useCallback(
    (files: GoodieFile[]) => {
      setLoading(true)

      const promises = files.map(function (file, index) {
        if (isNewFile(file)) {
          return upload(file, progress => onUpdateProgress(index, progress))
        }

        if (isDirtyFile(file)) return updateDirtyFile(file)

        return Promise.resolve(file)
      })

      return Promise.all(promises).finally(() => {
        setLoading(false)
        setProgress(null)
      })
    },
    [upload, updateDirtyFile, setLoading, onUpdateProgress]
  )

  return [update, { loading, progress }]
}

function isGoodieFileOrNull(
  key: string,
  value: unknown,
  keys?: string[]
): value is GoodieFile | null {
  if (keys && !keys.includes(key)) return false
  return isGoodieFile(value) || value === null
}

export function useUpdateNestedFiles<TResult extends object>() {
  const [updateFiles, { loading, progress }] = useUpdateFiles()

  const update = useCallback(
    async <TInput extends object>(
      object: TInput,
      includedKeys?: string[]
    ): Promise<TResult> => {
      return batchReplaceNestedFieldsWithIds<
        GoodieFile | null,
        TInput,
        TResult
      >({
        object,
        shouldUpdate: (key, value) =>
          isGoodieFileOrNull(key, value, includedKeys),
        batchUpdate: values => {
          const { filesToUpload, idxMapping } = values.reduce<{
            idxMapping: Record<number, number>
            filesToUpload: GoodieFile[]
          }>(
            function (acc, file, index) {
              if (file !== null) {
                const uploadIndex = acc.filesToUpload.push(file) - 1
                acc.idxMapping[index] = uploadIndex
              }

              return acc
            },
            { idxMapping: {}, filesToUpload: [] }
          )

          return updateFiles(filesToUpload).then(files =>
            values.map((value, index) =>
              value !== null ? files[idxMapping[index]].id : null
            )
          )
        }
      })
    },
    [updateFiles]
  )

  return [update, { loading, progress }] as const
}
